import React, { Fragment, useState, useEffect } from 'react';
import {
  GraduationCap,
  Plus,
  Edit2,
  Trash2,
  Users,
  Calendar,
  Clock,
  AlertTriangle,
  CheckCircle,
  Loader2,
  X,
  Award,
  UserPlus,
} from 'lucide-react';
import { trainingsApi, usersApi } from '../api';
import ExportButtons from '../components/ExportButtons';
import ColumnSelector from '../components/ColumnSelector';
import useColumnVisibility from '../hooks/useColumnVisibility';
import { toast } from 'sonner';
import { extractApiError, validateDateFields } from '../utils/dateValidation';

const TYPE_LABELS = {
  mandatory: { label: 'Obrigatório', color: 'bg-red-500/20 text-red-400' },
  optional: { label: 'Opcional', color: 'bg-gray-500/20 text-gray-400' },
  recycling: { label: 'Reciclagem', color: 'bg-yellow-500/20 text-yellow-400' },
  onboarding: { label: 'Integração', color: 'bg-blue-500/20 text-blue-400' },
  technical: { label: 'Técnico', color: 'bg-purple-500/20 text-purple-400' },
  behavioral: { label: 'Comportamental', color: 'bg-green-500/20 text-green-400' },
};

const STATUS_LABELS = {
  planned: { label: 'Planejado', color: 'bg-blue-500/20 text-blue-400' },
  in_progress: { label: 'Em Andamento', color: 'bg-yellow-500/20 text-yellow-400' },
  completed: { label: 'Concluído', color: 'bg-green-500/20 text-green-400' },
  cancelled: { label: 'Cancelado', color: 'bg-red-500/20 text-red-400' },
};

const MODALITY_LABELS = {
  in_person: 'Presencial',
  online: 'Online',
  hybrid: 'Híbrido',
  on_the_job: 'No Local de Trabalho',
};

const PARTICIPANT_STATUS = {
  enrolled: { label: 'Inscrito', color: 'bg-blue-500/20 text-blue-400' },
  attended: { label: 'Compareceu', color: 'bg-green-500/20 text-green-400' },
  absent: { label: 'Ausente', color: 'bg-red-500/20 text-red-400' },
  approved: { label: 'Aprovado', color: 'bg-green-500/20 text-green-400' },
  failed: { label: 'Reprovado', color: 'bg-red-500/20 text-red-400' },
};

const Trainings = () => {
  const [activeTab, setActiveTab] = useState('list');
  const [trainings, setTrainings] = useState([]);
  const [selectedTraining, setSelectedTraining] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [expiringCerts, setExpiringCerts] = useState([]);
  const [stats, setStats] = useState({ total_year: 0, completed: 0, planned: 0, expiring_soon: 0, expired: 0 });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showParticipantModal, setShowParticipantModal] = useState(false);
  const [editingTraining, setEditingTraining] = useState(null);
  const [filters, setFilters] = useState({ status: '', training_type: '' });
  const [users, setUsers] = useState([]);

  useEffect(() => {
    loadData();
    loadUsers();
  }, [filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [trainingsRes, statsRes, expiringRes] = await Promise.all([
        trainingsApi.list(filters),
        trainingsApi.stats(),
        trainingsApi.listExpiring(30),
      ]);
      setTrainings(trainingsRes.data);
      setStats(statsRes.data);
      setExpiringCerts(expiringRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const res = await usersApi.list({ limit: 100 });
      setUsers(res.data || []);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadParticipants = async (trainingId) => {
    try {
      const res = await trainingsApi.listParticipants(trainingId);
      setParticipants(res.data);
    } catch (error) {
      toast.error('Erro ao carregar participantes');
    }
  };

  const handleSelectTraining = async (training) => {
    setSelectedTraining(training);
    await loadParticipants(training.id);
    setActiveTab('participants');
  };

  const handleCreateTraining = async (data) => {
    try {
      await trainingsApi.create(data);
      toast.success('Treinamento criado com sucesso');
      setShowModal(false);
      loadData();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao criar treinamento'));
    }
  };

  const handleUpdateTraining = async (data) => {
    try {
      await trainingsApi.update(editingTraining.id, data);
      toast.success('Treinamento atualizado');
      setShowModal(false);
      setEditingTraining(null);
      loadData();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao atualizar'));
    }
  };

  const handleDeleteTraining = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este treinamento?')) return;
    try {
      await trainingsApi.delete(id);
      toast.success('Treinamento excluído');
      loadData();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const handleAddParticipant = async (userId) => {
    try {
      await trainingsApi.addParticipant(selectedTraining.id, { user_id: userId });
      toast.success('Participante adicionado');
      await loadParticipants(selectedTraining.id);
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao adicionar participante'));
    }
  };

  const handleUpdateParticipant = async (participantId, data) => {
    try {
      await trainingsApi.updateParticipant(participantId, data);
      toast.success('Participante atualizado');
      await loadParticipants(selectedTraining.id);
    } catch (error) {
      toast.error('Erro ao atualizar');
    }
  };

  const handleDeleteParticipant = async (participantId) => {
    if (!window.confirm('Tem certeza que deseja remover este participante do treinamento?')) return;
    try {
      await trainingsApi.deleteParticipant(participantId);
      toast.success('Participante removido com sucesso');
      await loadParticipants(selectedTraining.id);
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao remover participante'));
    }
  };

  const tabs = [
    { id: 'list', label: 'Treinamentos', icon: GraduationCap },
    { id: 'participants', label: 'Participantes', icon: Users, disabled: !selectedTraining },
    { id: 'expiring', label: 'Vencendo', icon: AlertTriangle },
    { id: 'pat', label: 'PAT', icon: Calendar },
  ];

  return (
    <div className="space-y-6" data-testid="trainings-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
            <GraduationCap className="text-green-500" />
            Treinamentos
          </h1>
          <p className="text-gray-400 mt-1">Capacitação e desenvolvimento de competências</p>
        </div>
        <div className="flex items-center gap-2">
          <ExportButtons module="trainings" />
          <button
            onClick={() => { setEditingTraining(null); setShowModal(true); }}
            className="btn-primary flex items-center gap-2"
            data-testid="add-training-btn"
          >
            <Plus size={20} />
            Novo Treinamento
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="card p-4">
          <p className="text-gray-400 text-sm">Total do Ano</p>
          <p className="text-2xl font-bold text-white">{stats.total_year}</p>
        </div>
        <div className="card p-4">
          <p className="text-gray-400 text-sm">Concluídos</p>
          <p className="text-2xl font-bold text-green-400">{stats.completed}</p>
        </div>
        <div className="card p-4">
          <p className="text-gray-400 text-sm">Planejados</p>
          <p className="text-2xl font-bold text-yellow-400">{stats.planned}</p>
        </div>
        <div className="card p-4">
          <p className="text-gray-400 text-sm">Vencendo (30d)</p>
          <p className="text-2xl font-bold text-orange-400">{stats.expiring_soon}</p>
        </div>
        <div className="card p-4">
          <p className="text-gray-400 text-sm">Vencidos</p>
          <p className="text-2xl font-bold text-red-400">{stats.expired}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-700">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => !tab.disabled && setActiveTab(tab.id)}
            disabled={tab.disabled}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === tab.id
                ? 'border-green-500 text-green-400'
                : tab.disabled
                ? 'border-transparent text-gray-600 cursor-not-allowed'
                : 'border-transparent text-gray-400 hover:text-white'
            }`}
          >
            <tab.icon size={18} />
            {tab.label}
            {tab.id === 'expiring' && stats.expiring_soon > 0 && (
              <span className="bg-orange-500 text-white text-xs px-1.5 py-0.5 rounded-full">{stats.expiring_soon}</span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="animate-spin text-green-500" size={32} />
          </div>
        ) : activeTab === 'list' ? (
          <TrainingsTable
            trainings={trainings}
            onSelect={handleSelectTraining}
            onEdit={(t) => { setEditingTraining(t); setShowModal(true); }}
            onDelete={handleDeleteTraining}
            filters={filters}
            setFilters={setFilters}
          />
        ) : activeTab === 'participants' ? (
          <ParticipantsView
            training={selectedTraining}
            participants={participants}
            users={users}
            onAddParticipant={handleAddParticipant}
            onUpdateParticipant={handleUpdateParticipant}
            onDeleteParticipant={handleDeleteParticipant}
          />
        ) : activeTab === 'expiring' ? (
          <ExpiringView certifications={expiringCerts} />
        ) : (
          <PATView trainings={trainings} year={new Date().getFullYear()} />
        )}
      </div>

      {/* Training Modal */}
      {showModal && (
        <TrainingModal
          training={editingTraining}
          users={users}
          onClose={() => { setShowModal(false); setEditingTraining(null); }}
          onSave={editingTraining ? handleUpdateTraining : handleCreateTraining}
        />
      )}
    </div>
  );
};

const TrainingsTable = ({ trainings, onSelect, onEdit, onDelete, filters, setFilters }) => {
  const COLUMNS = [
    { key: 'code', label: 'Código', locked: true, render: (t) => <td className="font-medium text-white font-mono">{t.code}</td> },
    { key: 'name', label: 'Nome', render: (t) => <td>{t.name}</td> },
    { key: 'type', label: 'Tipo', render: (t) => (
      <td>
        <span className={`px-2 py-1 rounded-full text-xs ${TYPE_LABELS[t.training_type]?.color || 'bg-gray-500/20 text-gray-400'}`}>
          {TYPE_LABELS[t.training_type]?.label || t.training_type}
        </span>
      </td>
    ) },
    { key: 'modality', label: 'Modalidade', render: (t) => <td>{MODALITY_LABELS[t.modality] || t.modality}</td> },
    { key: 'hours', label: 'Carga Horária', render: (t) => <td>{t.workload_hours}h</td> },
    { key: 'date', label: 'Data', render: (t) => <td>{t.scheduled_date || '-'}</td> },
    { key: 'status', label: 'Status', render: (t) => (
      <td>
        <span className={`px-2 py-1 rounded-full text-xs ${STATUS_LABELS[t.status]?.color || 'bg-gray-500/20 text-gray-400'}`}>
          {STATUS_LABELS[t.status]?.label || t.status}
        </span>
      </td>
    ) },
    { key: 'participants', label: 'Partic.', render: (t) => <td>{t.participants_count}</td> },
    { key: 'actions', label: 'Ações', locked: true, headerClass: 'text-right', render: (t) => (
      <td>
        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          <button onClick={() => onEdit(t)} className="p-2 text-gray-400 hover:text-blue-400 hover:bg-gray-700 rounded-lg">
            <Edit2 size={18} />
          </button>
          <button onClick={() => onDelete(t.id)} className="p-2 text-gray-400 hover:text-red-400 hover:bg-gray-700 rounded-lg">
            <Trash2 size={18} />
          </button>
        </div>
      </td>
    ) },
  ];

  const { visibleColumns, visibleKeys, toggleColumn, resetColumns } = useColumnVisibility({ storageKey: 'trainings', columns: COLUMNS });

  return (
  <div className="space-y-4">
    {/* Filters */}
    <div className="flex flex-col sm:flex-row gap-4">
      <input
        type="text"
        placeholder="Buscar treinamentos..."
        value={filters.search || ''}
        onChange={(e) => setFilters({ ...filters, search: e.target.value })}
        className="input px-3 w-full sm:w-60"
      />
      <select
        value={filters.status}
        onChange={(e) => setFilters({ ...filters, status: e.target.value })}
        className="input px-3 w-full sm:w-60"
      >
        <option value="">Todos os Status</option>
        {Object.entries(STATUS_LABELS).map(([key, { label }]) => (
          <option key={key} value={key}>{label}</option>
        ))}
      </select>
      <select
        value={filters.training_type}
        onChange={(e) => setFilters({ ...filters, training_type: e.target.value })}
        className="input px-3 w-full sm:w-60"
      >
        <option value="">Todos os Tipos</option>
        {Object.entries(TYPE_LABELS).map(([key, { label }]) => (
          <option key={key} value={key}>{label}</option>
        ))}
      </select>
      <div className="flex items-center">
        <ColumnSelector columns={COLUMNS} visibleKeys={visibleKeys} onToggle={toggleColumn} onReset={resetColumns} />
      </div>
    </div>

    {/* Table */}
    <div className="table-container">
      <table className="table">
        <thead>
          <tr>
            {visibleColumns.map(col => (
              <th key={col.key} className={col.headerClass || ''}>{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {trainings.length === 0 ? (
            <tr>
              <td colSpan={visibleColumns.length} className="text-center py-8 text-gray-500">Nenhum treinamento encontrado</td>
            </tr>
          ) : (
            trainings.map((t) => (
              <tr key={t.id} className="cursor-pointer" onClick={() => onSelect(t)}>
                {visibleColumns.map(col => (
                  <Fragment key={col.key}>{col.render(t)}</Fragment>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  </div>
  );
};

const ParticipantsView = ({ training, participants, users, onAddParticipant, onUpdateParticipant, onDeleteParticipant }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const enrolledUserIds = participants.map(p => p.user_id);
  const availableUsers = users.filter(u => !enrolledUserIds.includes(u.id));

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-white">{training.name}</h3>
          <p className="text-sm text-gray-400">{training.code} • {MODALITY_LABELS[training.modality]}</p>
        </div>
        <button onClick={() => setShowAddModal(true)} className="btn-primary" data-testid="add-participant-btn">
          <UserPlus size={18} />
          Adicionar Participante
        </button>
      </div>

      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Participante</th>
              <th>Email</th>
              <th>Status</th>
              <th>Nota</th>
              <th>Presença</th>
              <th>Vencimento</th>
              <th>Certificado</th>
              <th className="text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {participants.length === 0 ? (
              <tr>
                <td colSpan="8" className="text-center py-8 text-gray-500">Nenhum participante inscrito</td>
              </tr>
            ) : (
              participants.map((p) => (
                <tr key={p.id}>
                  <td className="font-medium text-white">{p.user_name}</td>
                  <td>{p.user_email}</td>
                  <td>
                    <select
                      value={p.status}
                      onChange={(e) => onUpdateParticipant(p.id, { status: e.target.value })}
                      className="input text-xs py-1 px-2"
                    >
                      {Object.entries(PARTICIPANT_STATUS).map(([key, { label }]) => (
                        <option key={key} value={key}>{label}</option>
                      ))}
                    </select>
                  </td>
                  <td>{p.score || '-'}</td>
                  <td>{p.attendance_percentage ? `${p.attendance_percentage}%` : '-'}</td>
                  <td>{p.expiration_date || '-'}</td>
                  <td>
                    {p.certificate_issued ? (
                      <span className="text-green-400"><Award size={18} /></span>
                    ) : (
                      <span className="text-gray-600"><Award size={18} /></span>
                    )}
                  </td>
                  <td>
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => onUpdateParticipant(p.id, { certificate_issued: !p.certificate_issued })}
                        className="text-xs text-blue-400 hover:underline"
                      >
                        {p.certificate_issued ? 'Revogar' : 'Emitir'} Cert.
                      </button>
                      <button
                        onClick={() => onDeleteParticipant(p.id)}
                        className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-gray-700 rounded-lg"
                        title="Remover participante"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showAddModal && (
        <>
          <div className="modal-backdrop" onClick={() => setShowAddModal(false)} />
          <div className="modal-content p-6 max-w-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-white">Adicionar Participante</h3>
              <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {availableUsers.length === 0 ? (
                <p className="text-gray-500 text-center py-4">Todos os usuários já estão inscritos</p>
              ) : (
                availableUsers.map((user) => (
                  <div
                    key={user.id}
                    onClick={() => { onAddParticipant(user.id); setShowAddModal(false); }}
                    className="p-3 bg-gray-700/50 rounded hover:bg-gray-700 cursor-pointer"
                  >
                    <p className="text-white">{user.full_name}</p>
                    <p className="text-gray-400 text-sm">{user.email}</p>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const ExpiringView = ({ certifications }) => (
  <div className="space-y-4">
    <h3 className="text-lg font-semibold text-white">Certificações Vencendo nos Próximos 30 Dias</h3>
    <div className="table-container">
      <table className="table">
        <thead>
          <tr>
            <th>Colaborador</th>
            <th>Email</th>
            <th>Vencimento</th>
            <th>Certificado</th>
          </tr>
        </thead>
        <tbody>
          {certifications.length === 0 ? (
            <tr>
              <td colSpan="4" className="text-center py-8 text-gray-500">Nenhuma certificação vencendo</td>
            </tr>
          ) : (
            certifications.map((c) => (
              <tr key={c.id}>
                <td className="font-medium text-white">{c.user_name}</td>
                <td>{c.user_email}</td>
                <td className="text-orange-400 font-semibold">{c.expiration_date}</td>
                <td>{c.certificate_number || '-'}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  </div>
);

const MONTHS_PT = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

const PATView = ({ trainings, year }) => {
  const byMonth = Array.from({ length: 12 }, (_, i) => ({
    month: i,
    items: trainings.filter(t => {
      if (!t.scheduled_date) return false;
      const d = new Date(t.scheduled_date + 'T00:00:00');
      return d.getFullYear() === year && d.getMonth() === i;
    }),
  }));

  const statusColor = (status) => {
    if (status === 'completed') return 'bg-green-500/20 text-green-400 border-green-500/30';
    if (status === 'in_progress') return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    if (status === 'cancelled') return 'bg-red-500/20 text-red-400 border-red-500/30';
    return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
  };

  return (
    <div className="p-4 space-y-4">
      <h3 className="text-lg font-semibold text-white">Plano Anual de Treinamentos — {year}</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {byMonth.map(({ month, items }) => (
          <div key={month} className="bg-gray-800 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white font-semibold">{MONTHS_PT[month]}</span>
              <span className="text-gray-500 text-xs">{items.length} treinamento{items.length !== 1 ? 's' : ''}</span>
            </div>
            {items.length === 0 ? (
              <p className="text-gray-600 text-xs">Nenhum planejado</p>
            ) : (
              <ul className="space-y-1">
                {items.map(t => (
                  <li key={t.id} className={`text-xs px-2 py-1 rounded border ${statusColor(t.status)}`}>
                    <span className="font-medium">{t.name}</span>
                    {t.workload_hours && <span className="ml-1 opacity-70">({t.workload_hours}h)</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
      <div className="flex gap-4 text-xs text-gray-500 pt-2">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-yellow-500/40 inline-block" />Planejado</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-500/40 inline-block" />Em Andamento</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-500/40 inline-block" />Concluído</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-500/40 inline-block" />Cancelado</span>
      </div>
    </div>
  );
};

const TrainingModal = ({ training, users, onClose, onSave }) => {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: training?.name || '',
    description: training?.description || '',
    training_type: training?.training_type || 'mandatory',
    modality: training?.modality || 'in_person',
    category: training?.category || '',
    objectives: training?.objectives || '',
    workload_hours: training?.workload_hours || 1,
    validity_months: training?.validity_months || '',
    requires_evaluation: training?.requires_evaluation || false,
    minimum_score: training?.minimum_score || '',
    instructor_name: training?.instructor_name || '',
    external_provider: training?.external_provider || '',
    scheduled_date: training?.scheduled_date || '',
    end_date: training?.end_date || '',
    location: training?.location || '',
    max_participants: training?.max_participants || '',
    responsible_id: training?.responsible_id || '',
    status: training?.status || 'planned',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const dateError = validateDateFields([
      { value: formData.scheduled_date, label: 'Data Início' },
      { value: formData.end_date, label: 'Data Fim' },
    ]);
    if (dateError) { toast.error(dateError); return; }
    if (!formData.scheduled_date || !formData.end_date) {
      toast.error('Preencha a Data Início e a Data Fim para continuar');
      return;
    }
    setSaving(true);
    const data = { ...formData };
    if (!data.validity_months) delete data.validity_months;
    else data.validity_months = parseInt(data.validity_months);
    if (!data.minimum_score) delete data.minimum_score;
    else data.minimum_score = parseFloat(data.minimum_score);
    if (!data.max_participants) delete data.max_participants;
    else data.max_participants = parseInt(data.max_participants);
    if (!data.responsible_id) delete data.responsible_id;
    try {
      await onSave(data);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-content p-6 max-w-2xl">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">
            {training ? 'Editar Treinamento' : 'Novo Treinamento'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={24} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-2">Nome *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="input w-full"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Tipo</label>
              <select
                value={formData.training_type}
                onChange={(e) => setFormData({ ...formData, training_type: e.target.value })}
                className="input w-full"
              >
                {Object.entries(TYPE_LABELS).map(([key, { label }]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Modalidade</label>
              <select
                value={formData.modality}
                onChange={(e) => setFormData({ ...formData, modality: e.target.value })}
                className="input w-full"
              >
                {Object.entries(MODALITY_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Categoria</label>
              <input
                type="text"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="input w-full"
                placeholder="Ex: NR, Qualidade, Processo"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Carga Horária (h)</label>
              <input
                type="number"
                min="1"
                value={formData.workload_hours}
                onChange={(e) => setFormData({ ...formData, workload_hours: parseInt(e.target.value) || 1 })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Validade (meses)</label>
              <input
                type="number"
                min="1"
                value={formData.validity_months}
                onChange={(e) => setFormData({ ...formData, validity_months: e.target.value })}
                className="input w-full"
                placeholder="Deixe vazio se não tiver validade"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Instrutor</label>
              <input
                type="text"
                value={formData.instructor_name}
                onChange={(e) => setFormData({ ...formData, instructor_name: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Fornecedor Externo</label>
              <input
                type="text"
                value={formData.external_provider}
                onChange={(e) => setFormData({ ...formData, external_provider: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Data Início *</label>
              <input
                type="date"
                value={formData.scheduled_date}
                onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                className="input w-full"
                min="1900-01-01"
                max="2099-12-31"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Data Fim *</label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                className="input w-full"
                min="1900-01-01"
                max="2099-12-31"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Local</label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Máx. Participantes</label>
              <input
                type="number"
                min="1"
                value={formData.max_participants}
                onChange={(e) => setFormData({ ...formData, max_participants: e.target.value })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Responsável</label>
              <select
                value={formData.responsible_id}
                onChange={(e) => setFormData({ ...formData, responsible_id: e.target.value })}
                className="input w-full"
              >
                <option value="">Selecione...</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>{user.full_name}</option>
                ))}
              </select>
            </div>
            {training && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="input w-full"
                >
                  {Object.entries(STATUS_LABELS).map(([key, { label }]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="md:col-span-2 flex gap-4">
              <label className="flex items-center gap-2 text-gray-400">
                <input
                  type="checkbox"
                  checked={formData.requires_evaluation}
                  onChange={(e) => setFormData({ ...formData, requires_evaluation: e.target.checked })}
                  className="form-checkbox text-green-500"
                />
                Requer Avaliação
              </label>
              {formData.requires_evaluation && (
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-400">Nota Mínima:</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.minimum_score}
                    onChange={(e) => setFormData({ ...formData, minimum_score: e.target.value })}
                    className="input w-20"
                  />
                </div>
              )}
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-2">Objetivos</label>
              <textarea
                value={formData.objectives}
                onChange={(e) => setFormData({ ...formData, objectives: e.target.value })}
                className="input w-full"
                rows={2}
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-2">Descrição</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="input w-full"
                rows={2}
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
            <button type="button" onClick={onClose} className="btn-secondary" disabled={saving}>Cancelar</button>
            <button type="submit" className="btn-primary flex items-center gap-2" disabled={saving}>
              {saving ? <><Loader2 size={18} className="animate-spin" /> Salvando...</> : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default Trainings;
